import React, { createContext, useContext, useEffect, useState } from 'react';
import Layout from './Layout';
import { pl } from '../services/api';
import { Lock } from 'lucide-react';

const PLLockContext = createContext<{ enabled: boolean; lockNow: () => void } | null>(null);

export const usePLLockControl = () => useContext(PLLockContext);

const PLLock: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [checking, setChecking] = useState(true);
  const [enabled, setEnabled] = useState(false);
  const [unlocked, setUnlocked] = useState(false);
  const [pin, setPin] = useState('');
  const [error, setError] = useState('');
  const [submitting, setSubmitting] = useState(false);

  const checkStatus = () => {
    setChecking(true);
    pl.lockStatus()
      .then(r => { setEnabled(!!r.data.enabled); setUnlocked(!r.data.enabled || r.data.unlocked); })
      .catch(() => setUnlocked(true))
      .finally(() => setChecking(false));
  };

  useEffect(() => { checkStatus(); }, []);

  const handleUnlock = async () => {
    if (!pin.trim()) return;
    setSubmitting(true);
    setError('');
    try {
      await pl.unlock(pin.trim());
      setPin('');
      setUnlocked(true);
    } catch {
      setError('Incorrect PIN');
    }
    setSubmitting(false);
  };

  const lockNow = () => {
    pl.lock().catch(() => {});
    setUnlocked(false);
  };

  if (checking) return null;

  if (unlocked) {
    return <PLLockContext.Provider value={{ enabled, lockNow }}>{children}</PLLockContext.Provider>;
  }

  return (
    <Layout title="P&L Report" subtitle="Locked">
      <div style={{ display: 'flex', alignItems: 'center', justifyContent: 'center', minHeight: '60vh' }}>
        <div style={{ width: 300, textAlign: 'center' }}>
          <div style={{ width: 48, height: 48, borderRadius: 12, background: 'var(--accent-blue-soft)', display: 'flex', alignItems: 'center', justifyContent: 'center', margin: '0 auto 16px' }}>
            <Lock size={20} color="var(--accent-blue)" />
          </div>
          <div style={{ fontSize: 15, fontWeight: 600, color: 'var(--text-primary)', marginBottom: 4 }}>P&L is locked</div>
          <div style={{ fontSize: 12.5, color: 'var(--text-muted)', marginBottom: 20 }}>Enter the PIN to view this section.</div>
          <input
            type="password"
            inputMode="numeric"
            autoFocus
            value={pin}
            onChange={e => { setPin(e.target.value); setError(''); }}
            onKeyDown={e => { if (e.key === 'Enter') handleUnlock(); }}
            placeholder="PIN"
            style={{ width: '100%', fontSize: 15, padding: '10px 12px', borderRadius: 8, border: `1px solid ${error ? 'var(--accent-red)' : 'var(--border)'}`, background: 'var(--bg-elevated)', color: 'var(--text-primary)', outline: 'none', textAlign: 'center', letterSpacing: 4, marginBottom: 10 }}
          />
          {error && <div style={{ fontSize: 12, color: 'var(--accent-red)', marginBottom: 10 }}>{error}</div>}
          <button
            onClick={handleUnlock}
            disabled={submitting || !pin.trim()}
            style={{ width: '100%', background: 'var(--accent-blue)', border: 'none', borderRadius: 8, padding: '10px', cursor: 'pointer', fontSize: 13.5, color: '#fff', fontWeight: 600, opacity: submitting || !pin.trim() ? 0.5 : 1 }}
          >
            {submitting ? 'Checking…' : 'Unlock'}
          </button>
        </div>
      </div>
    </Layout>
  );
};

export default PLLock;
